import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { speicher } from '../useUebung'
import { heute as heuteIso } from '../scheduler/datum'
import { HERDENPFERDE } from '../herde/pferde'
import { herdeStufen, mitGedaechtnis, STUFEN_TEXT, type Stufe } from '../herde/herde'
import type { Profil } from '../speicher/typen'
import { tonAufwecken } from '../ton'
import { Sterne } from '../spiel/Deko'

const STUFEN_ZEICHEN: Record<Stufe, string> = { 0: '❔', 1: '🐾', 2: '👀', 3: '🤝', 4: '🏡' }

/**
 * Die Talkarte des Sternentals.
 *
 * Zeigt für jede Reihe, wie weit ihr Pferd ist. Die Stufe kommt allein aus
 * den Kartenständen, hier wird nichts gezählt oder vergeben.
 */
export function Herde() {
  const navigate = useNavigate()
  const [profil, setProfil] = useState<Profil | null>(null)
  const [name, setName] = useState('')
  const [fehler, setFehler] = useState(false)
  // Für das Namensformular: Das Speichern kann erst ankommen, wenn Lina die
  // Seite schon verlassen hat.
  const gemountet = useRef(true)
  useEffect(() => {
    gemountet.current = true
    return () => {
      gemountet.current = false
    }
  }, [])

  useEffect(() => {
    // Schutz gegen das Setzen von Zustand nach dem Verlassen der Seite,
    // falls das Laden erst nach dem Unmount durchkommt.
    let aktiv = true
    void (async () => {
      try {
        const [p, staende] = await Promise.all([speicher.profilLaden(), speicher.staendeLaden(heuteIso())])
        const herde = mitGedaechtnis(herdeStufen(staende), p.herde)
        const neu = { ...p, herde }
        try {
          // Speichern darf die Talkarte nie verhindern, siehe abschliessen()
          // in useUebung.ts: Scheitert es, sieht das Kind die Karte trotzdem,
          // nur der nächste Besuch startet ohne die frische Stufe.
          await speicher.profilSpeichern(neu)
        } catch (speicherFehler) {
          console.error('[zahlenflug] Herde konnte nicht gespeichert werden:', speicherFehler)
        }
        if (aktiv) setProfil(neu)
      } catch (ladeFehler) {
        console.error('[zahlenflug] Talkarte konnte nicht geladen werden:', ladeFehler)
        if (aktiv) setFehler(true)
      }
    })()
    return () => {
      aktiv = false
    }
  }, [])

  if (fehler) {
    return (
      <div className="buehne">
        <Sterne />
        <div className="mitte">
          <p className="unterzeile">Das Sternental lässt sich gerade nicht öffnen. Lade die Seite bitte neu.</p>
          <Link className="knopf knopf--leise" to="/">
            Zurück
          </Link>
        </div>
      </div>
    )
  }

  if (!profil) {
    return (
      <div className="buehne">
        <Sterne />
        <div className="mitte">
          <p className="unterzeile">Einen Moment …</p>
        </div>
      </div>
    )
  }

  if (!profil.fohlenName) {
    return (
      <div className="buehne">
        <Sterne />
        <form
          className="mitte"
          onSubmit={(e) => {
            e.preventDefault()
            const n = name.trim()
            if (!n) return
            const neu = { ...profil, fohlenName: n }
            // Der Name gilt für diesen Besuch auch, wenn das Speichern
            // scheitert. Das Kind darf hier nicht hängen bleiben.
            void speicher
              .profilSpeichern(neu)
              .catch((fehler) => console.error('[zahlenflug] Name konnte nicht gespeichert werden:', fehler))
              .finally(() => {
                if (gemountet.current) setProfil(neu)
              })
          }}
        >
          <h1 className="titel">Ein Fohlen im Nebel</h1>
          <p className="unterzeile">
            Nach dem Sturm liegt ein kleines Fohlen im Gras und zittert. Es bleibt bei dir. Wie soll es heißen?
          </p>
          <input
            className="code-eingabe code-eingabe--name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 16))}
            aria-label="Name des Fohlens"
            autoFocus
          />
          <button className="knopf" type="submit" disabled={!name.trim()}>
            So soll es heißen
          </button>
        </form>
      </div>
    )
  }

  const daheim = HERDENPFERDE.filter((p) => (profil.herde[p.reihe] ?? 0) === 4).length

  return (
    <div className="buehne buehne--scroll">
      <Sterne />
      <div className="kopfzeile">
        <Link className="knopf knopf--leise" to="/">
          Zurück
        </Link>
      </div>
      <div className="mitte">
        <h1 className="titel">Das Sternental</h1>
        <p className="unterzeile">
          {daheim === 0
            ? `Die Herde ist noch verstreut. ${profil.fohlenName} hilft dir suchen.`
            : `${daheim} von 10 Pferden ${daheim === 1 ? 'ist' : 'sind'} wieder zu Hause.`}
        </p>

        <div className="herde">
          {HERDENPFERDE.map((p) => {
            const stufe = profil.herde[p.reihe] ?? 0
            return (
              <div key={p.reihe} className={`herde__gebiet herde__gebiet--${stufe}`}>
                <span className="herde__zeichen" aria-hidden="true">
                  {STUFEN_ZEICHEN[stufe]}
                </span>
                <span className="herde__name">{stufe >= 2 ? p.name : '???'}</span>
                <span className="herde__ort">
                  {p.gebiet} · {p.reihe}er
                </span>
                <span className="herde__stufe">{STUFEN_TEXT[stufe]}</span>
              </div>
            )
          })}
        </div>

        <button
          className="knopf"
          type="button"
          onClick={() => {
            // Der Audiokontext darf nur aus einer Berührung heraus starten.
            tonAufwecken()
            navigate('/ritt')
          }}
        >
          Ausreiten mit {profil.fohlenName}
        </button>
      </div>
    </div>
  )
}
